import { useState, useEffect } from "react";
import ModalTemplate from "../modal/modalTemplate";
import "./FilesView.css";

const FilesView = ({ show, handleClose, title, files = [], selectedId = 0 }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    // Reset to the correct file when files or selectedId changes
    useEffect(() => {
        if (Array.isArray(files) && files.length > 0) {
            const selectedIndex = Number(selectedId);
            const safeIndex = Number.isInteger(selectedIndex)
                ? Math.min(Math.max(selectedIndex, 0), files.length - 1)
                : 0;
            setCurrentIndex(safeIndex);
        } else {
            setCurrentIndex(0);
        }
    }, [selectedId, files]);

    // If no files, return nothing
    if (!Array.isArray(files) || files.length === 0) return null;

    const currentFile = files[currentIndex];

    // ✅ If currentFile is undefined (edge case during updates), show loading or fallback
    if (!currentFile) {
        return (
            <ModalTemplate
                show={show}
                closeFun={handleClose}
                centered
                title={title}
                body={<p className="text-center py-5">Loading file...</p>}
                footer={null}
            />
        );
    }

    const isImageFile = (fileName = "") => {
        const ext = fileName.split("?")[0].split(".").pop()?.toLowerCase();
        return ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
    };

    const isImage = isImageFile(currentFile.fileName);

    const handleNext = () => {
        setCurrentIndex((prev) => (prev + 1) % files.length);
    };

    const handleBack = () => {
        setCurrentIndex((prev) => (prev - 1 + files.length) % files.length);
    };

    return (
        <ModalTemplate
            show={show}
            closeFun={handleClose}
            centered
            size="lg"
            title={title}
            isRemoveBodyPadding
            body={
                <div className="files-view">
                    <div className="files-view__toolbar">
                        <div className="files-view__file-details">
                            <span className="material-symbols-rounded" aria-hidden="true">
                                {isImage ? "image" : "description"}
                            </span>
                            <span title={currentFile.fileName}>{currentFile.fileName || "Untitled file"}</span>
                        </div>
                        <span className="files-view__counter" aria-live="polite">
                            {currentIndex + 1} of {files.length}
                        </span>
                    </div>

                    <div className="files-view__stage">
                        <button
                            type="button"
                            className="files-view__arrow files-view__arrow--previous"
                            onClick={handleBack}
                            aria-label="View previous file"
                            disabled={files.length === 1}
                        >
                            <i className="material-symbols-rounded" aria-hidden="true">chevron_left</i>
                        </button>

                        <div className="files-view__preview">
                            {isImage ? (
                                <img
                                    src={currentFile.preview}
                                    alt={currentFile.fileName || "file"}
                                    className="files-view__image"
                                />
                            ) : (
                                <iframe
                                    src={currentFile.preview}
                                    title={currentFile.fileName || "file"}
                                    className="files-view__document"
                                />
                            )}
                        </div>

                        <button
                            type="button"
                            className="files-view__arrow files-view__arrow--next"
                            onClick={handleNext}
                            aria-label="View next file"
                            disabled={files.length === 1}
                        >
                            <i className="material-symbols-rounded" aria-hidden="true">chevron_right</i>
                        </button>
                    </div>

                    <div className="files-view__footer">
                        <span className="files-view__footer-hint">
                            Use the arrows to browse photos
                        </span>
                        <a
                            className="files-view__open-original"
                            href={currentFile.preview}
                            target="_blank"
                            rel="noreferrer"
                        >
                            Open original
                            <i className="material-symbols-rounded" aria-hidden="true">open_in_new</i>
                        </a>
                    </div>
                </div>
            }
            footer={null}
        />
    );
};

export default FilesView;
